import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { AgentTrustReceipt } from './agentTrustReceiptModel.ts';

const i18n = defineMessages({
	heading: {
		id: 'agent-trust-receipt.panel.heading',
		defaultMessage: 'Agent activity receipt',
		description: 'Heading for the Agent Trust Receipt object sidebar panel.',
	},
	actorLabel: {
		id: 'agent-trust-receipt.panel.actor-label',
		defaultMessage: 'Actor',
		description: 'Label for the actor named in an Agent Trust Receipt.',
	},
	scenarioLabel: {
		id: 'agent-trust-receipt.panel.scenario-label',
		defaultMessage: 'Scenario',
		description: 'Label for the scenario named in an Agent Trust Receipt.',
	},
});

const styles = cssMap({
	container: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.300'),
	},
	section: {
		paddingBlockStart: token('space.150'),
	},
	signalRow: {
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockStart: token('space.100'),
	},
});

type AgentTrustReceiptPanelBodyProps = {
	receipt: AgentTrustReceipt;
};

export const AgentTrustReceiptPanelBody: FC<AgentTrustReceiptPanelBodyProps> = ({ receipt }) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.container} testId="agent-trust-receipt-panel-body">
			<Stack space="space.250">
				<Stack space="space.100">
					<Text size="large" weight="semibold">
						{formatMessage(i18n.heading)}
					</Text>
					<Text>{receipt.summary}</Text>
				</Stack>
				<Stack space="space.100" xcss={styles.section}>
					<Inline spread="space-between" alignBlock="start">
						<Text weight="semibold">{formatMessage(i18n.actorLabel)}</Text>
						<Text>{receipt.actor.name}</Text>
					</Inline>
					<Inline spread="space-between" alignBlock="start">
						<Text weight="semibold">{formatMessage(i18n.scenarioLabel)}</Text>
						<Text>{receipt.scenario.label}</Text>
					</Inline>
				</Stack>
				<Stack space="space.150">
					{receipt.signals.map((signal) => (
						<Inline
							key={signal.id}
							spread="space-between"
							alignBlock="start"
							xcss={styles.signalRow}
						>
							<Text weight="semibold">{signal.label}</Text>
							<Text>{signal.value}</Text>
						</Inline>
					))}
				</Stack>
			</Stack>
		</Box>
	);
};
